import { useEffect, useState } from 'react';
import countriesList from 'countries-list';
import { regionList } from '@general/helpers/regions.helpers';
import { ConnectionsSlugStrings } from '@general/helpers/SavedSearchesQuestionStrings';
import useViewer from '@hooks/useViewer';

const arrayFilterStrings = (filterKeyName, filterArray) =>
    filterArray.map(item => `${filterKeyName}:${item.toLowerCase().replace('_', '')}`);

const generatePrimaryInterestString = search => {
    let primaryInterest;

    if (search.primaryInterest === 'CONNECTIONS') return '';
    if (search.primaryInterest === 'TENANTS') primaryInterest = 'space';
    if (search.primaryInterest === 'SPACE') primaryInterest = 'tenants';
    if (search.primaryInterest === 'PRODUCTS') primaryInterest = 'products';

    return `savedSearch.primaryInterest:${primaryInterest}`;
};

const generateConnectionsFilters = search => [
    `personalResponsibility:${ConnectionsSlugStrings.connectionType[search.connections.connectionType]}`,
];

const generateProductsFilters = search => {
    const areYou = search.products.areYou === 'LOOKING' ? 'providing' : 'looking';

    return [
        `savedSearch.products.areYou:${areYou}`,
        `savedSearch.products.productTypes:${search.products.productTypes}`,
    ];
};

const generateSpaceOrTenantsFilters = (search, kind, sourceKind) => [
    arrayFilterStrings(`savedSearch.${kind}.purpose`, search[sourceKind].purpose),
    arrayFilterStrings(`savedSearch.${kind}.density`, search[sourceKind].density),
    arrayFilterStrings(`savedSearch.${kind}.size`, search[sourceKind].size),
    arrayFilterStrings(`savedSearch.${kind}.typeOfCenter`, search[sourceKind].typeOfCenter),
    arrayFilterStrings(`savedSearch.${kind}.typeOfFacility`, search[sourceKind].typeOfFacility),
];

const generateLocationFilter = search => {
    let locationsString = '';

    if (!search.businessLocations) {
        return locationsString;
    }

    if (search.primaryInterest === 'CONNECTIONS') {
        locationsString = search.businessLocations
            .map(location => {
                if (location === 'CANADA') {
                    return 'country:Canada';
                }

                if (location === 'INTERNATIONAL') {
                    const countries = Object.keys(countriesList.countries)
                        .filter(countryKey => !['US', 'CA'].includes(countryKey))
                        .map(countryKey => countriesList.countries[countryKey].name);

                    return countries.map(country => `country:"${country}"`).join(' OR ');
                }

                return regionList[location].subregions.map(subregion => `stateCode:"${subregion.abbr}"`).join(' OR ');
            })
            .join(' OR ');
    } else {
        locationsString = search.businessLocations
            .map(location => `savedSearch.businessLocations:${location.toLowerCase()}`)
            .join(' OR ');
    }

    if (locationsString === '' || locationsString == []) {
        return locationsString;
    }

    return `(${locationsString})`;
};

export const generateFiltersFromSavedSearch = (search, additionalFilters = [], viewer = null, newOnly = false) => {
    const filtersArray = [
        ...additionalFilters,
        generatePrimaryInterestString(search),
        generateLocationFilter(search),
    ].filter(item => !!item.length);
    if (viewer && filtersArray.length > 0) {
        filtersArray.push(`NOT memberId:"${viewer.id}"`);
        filtersArray.push('status:Active');
    }
    return filtersArray.join(' AND ');
};

export const generateOptionalFiltersFromSavedSearch = search => {
    const optionalFilters = [];

    if (search.primaryInterest === 'CONNECTIONS') {
        optionalFilters.push(...generateConnectionsFilters(search));
    }

    if (search.primaryInterest === 'TENANTS') {
        optionalFilters.push(...generateSpaceOrTenantsFilters(search, 'space', 'tenants'));
    }

    if (search.primaryInterest === 'SPACE') {
        optionalFilters.push(...generateSpaceOrTenantsFilters(search, 'tenants', 'space'));
    }

    if (search.primaryInterest === 'PRODUCTS') {
        optionalFilters.push(...generateProductsFilters(search));
    }

    return optionalFilters.filter(item => !!item.length);
};

const useMemberMatchFilters = (savedSearch, additionalFilters = '') => {
    const { viewer } = useViewer();
    const [filters, setFilters] = useState('');
    const [optionalFilters, setOptionalFilters] = useState([]);

    useEffect(() => {
        if (!viewer || !savedSearch) {
            return;
        }

        let filterString = generateFiltersFromSavedSearch(savedSearch, [], viewer);

        if (filterString.length > 0 && additionalFilters.length > 0) {
            filterString += ' AND ';
        }

        if (additionalFilters.length > 0) {
            filterString += additionalFilters;
        }

        setFilters(filterString);
        setOptionalFilters(generateOptionalFiltersFromSavedSearch(savedSearch));
    }, [viewer, savedSearch, additionalFilters]);

    return { filters, optionalFilters };
};

export default useMemberMatchFilters;
